import { FormEvent, useEffect, useMemo, useState } from "react";
import type { Unsubscribe } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import {
  listSessionPublicDecks,
  subscribeSessionPublicDecks,
} from "../services/accessProjectionService";
import {
  listAccessibleTrainingSessions,
  subscribeSessionRuntimeState,
  updateSessionSlideNavigation,
} from "../services/sessionService";
import type { SessionPublicDeck } from "../types/access";
import type { SessionRuntimeState, TrainingSession } from "../types/session";
import { SessionExerciseRuntimePanel } from "./SessionExerciseRuntimePanel";
import { SessionQuestionnaireRuntimePanel } from "./SessionQuestionnaireRuntimePanel";

function trainerHashParams(): URLSearchParams {
  if (!window.location.hash.startsWith("#/trainer")) {
    return new URLSearchParams();
  }

  const queryString = window.location.hash.split("?")[1] ?? "";
  return new URLSearchParams(queryString);
}

function replaceTrainerHash({
  deckId,
  page,
  sessionId,
}: {
  deckId: string | null;
  page: number | null;
  sessionId: string | null;
}) {
  const params = new URLSearchParams();

  if (sessionId) {
    params.set("session", sessionId);
  }

  if (deckId) {
    params.set("deck", deckId);
  }

  if (page !== null) {
    params.set("page", String(page));
  }

  const nextHash = params.size > 0 ? `#/trainer?${params.toString()}` : "#/trainer";
  window.history.replaceState(null, "", nextHash);
}

function clampPage(value: number, pageCount: number): number {
  return Math.min(Math.max(0, Math.trunc(value)), Math.max(0, pageCount));
}

function pageLabel(page: number, pageCount: number): string {
  return page === 0 ? "Accueil" : `Page ${page}/${pageCount}`;
}

export function SessionSlideshowWorkspace() {
  const { isGlobalAdmin, user } = useAuth();
  const [decks, setDecks] = useState<SessionPublicDeck[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [manualVisibleMaxPage, setManualVisibleMaxPage] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [runtime, setRuntime] = useState<SessionRuntimeState | null>(null);
  const [selectedDeckId, setSelectedDeckId] = useState<string | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [sessions, setSessions] = useState<TrainingSession[]>([]);
  const [shouldWriteTrainerHash, setShouldWriteTrainerHash] = useState(
    window.location.hash.startsWith("#/trainer"),
  );
  const [targetPage, setTargetPage] = useState("0");

  const activeSessions = useMemo(
    () => sessions.filter((session) => session.status === "active"),
    [sessions],
  );
  const selectedSession = useMemo(
    () =>
      activeSessions.find((session) => session.id === selectedSessionId) ?? null,
    [activeSessions, selectedSessionId],
  );
  const selectedDeck = useMemo(
    () => decks.find((deck) => deck.id === selectedDeckId) ?? null,
    [decks, selectedDeckId],
  );
  const currentPage =
    runtime && selectedDeck && runtime.currentDeckId === selectedDeck.id
      ? runtime.currentPage
      : 0;
  const mayNavigate = Boolean(user && selectedSession && selectedDeck && runtime);

  useEffect(() => {
    if (!user) {
      setDecks([]);
      setRuntime(null);
      setSelectedDeckId(null);
      setSelectedSessionId(null);
      setSessions([]);
      return;
    }

    setIsLoading(true);
    setMessage(null);

    void listAccessibleTrainingSessions({
      isGlobalAdmin,
      uid: user.uid,
    })
      .then((nextSessions) => {
        const nextActiveSessions = nextSessions.filter(
          (session) => session.status === "active",
        );
        const requestedSessionId = trainerHashParams().get("session");

        setSessions(nextSessions);
        setSelectedSessionId((currentId) => {
          if (
            currentId &&
            nextActiveSessions.some((session) => session.id === currentId)
          ) {
            return currentId;
          }

          if (
            requestedSessionId &&
            nextActiveSessions.some((session) => session.id === requestedSessionId)
          ) {
            return requestedSessionId;
          }

          return nextActiveSessions[0]?.id ?? null;
        });
      })
      .catch((error: unknown) => {
        console.error(error);
        setMessage("Impossible de charger les sessions actives.");
      })
      .finally(() => setIsLoading(false));
  }, [isGlobalAdmin, user]);

  useEffect(() => {
    if (!selectedSessionId) {
      setDecks([]);
      setRuntime(null);
      setSelectedDeckId(null);
      return undefined;
    }

    setIsLoading(true);
    setMessage(null);

    const unsubscribers: Unsubscribe[] = [
      subscribeSessionRuntimeState({
        onError: (error) => {
          console.error(error);
          setMessage("Impossible de suivre l'état slideshow.");
        },
        onNext: setRuntime,
        sessionId: selectedSessionId,
      }),
      subscribeSessionPublicDecks({
        onError: (error) => {
          console.error(error);
          setMessage("Impossible de suivre les supports publics.");
        },
        onNext: (nextDecks) => {
          setDecks(nextDecks);
          setIsLoading(false);
        },
        sessionId: selectedSessionId,
      }),
    ];

    void listSessionPublicDecks(selectedSessionId)
      .then((nextDecks) => {
        setDecks(nextDecks);
        setIsLoading(false);
      })
      .catch((error: unknown) => {
        console.error(error);
        setMessage("Impossible de charger les supports slideshow.");
        setIsLoading(false);
      });

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [selectedSessionId]);

  useEffect(() => {
    const requestedDeckId = trainerHashParams().get("deck");
    const runtimeDeckId = runtime?.currentDeckId ?? null;
    const preferredDeckId =
      (requestedDeckId && decks.some((deck) => deck.id === requestedDeckId)
        ? requestedDeckId
        : null) ??
      (runtimeDeckId && decks.some((deck) => deck.id === runtimeDeckId)
        ? runtimeDeckId
        : null) ??
      decks[0]?.id ??
      null;

    setSelectedDeckId((currentId) =>
      currentId && decks.some((deck) => deck.id === currentId)
        ? currentId
        : preferredDeckId,
    );
  }, [decks, runtime]);

  useEffect(() => {
    const hashPage = Number(trainerHashParams().get("page") ?? currentPage);
    const nextPage = selectedDeck
      ? clampPage(
          Number.isFinite(hashPage) ? hashPage : currentPage,
          selectedDeck.pageCount,
        )
      : 0;

    setTargetPage(String(nextPage));
  }, [currentPage, selectedDeck]);

  useEffect(() => {
    if (!shouldWriteTrainerHash) {
      return;
    }

    replaceTrainerHash({
      deckId: selectedDeckId,
      page: selectedDeck ? currentPage : null,
      sessionId: selectedSessionId,
    });
  }, [
    currentPage,
    selectedDeck,
    selectedDeckId,
    selectedSessionId,
    shouldWriteTrainerHash,
  ]);

  async function navigateTo({
    manualVisiblePage,
    page,
  }: {
    manualVisiblePage?: number;
    page: number;
  }) {
    if (!user || !selectedSession || !selectedDeck) {
      return;
    }

    setShouldWriteTrainerHash(true);
    setIsSaving(true);
    setMessage(null);

    try {
      const navigationRequest = {
        actorUid: user.uid,
        deckId: selectedDeck.id,
        page,
        sessionId: selectedSession.id,
      };
      const nextRuntime = await updateSessionSlideNavigation(
        manualVisiblePage === undefined
          ? navigationRequest
          : {
              ...navigationRequest,
              manualVisibleMaxPage: manualVisiblePage,
            },
      );
      setRuntime(nextRuntime);
      setManualVisibleMaxPage("");
      setMessage("Navigation slideshow mise à jour.");
    } catch (error: unknown) {
      console.error(error);
      setMessage("Impossible de mettre à jour la navigation slideshow.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleTargetPage(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!selectedDeck) {
      return;
    }

    await navigateTo({
      page: clampPage(Number(targetPage), selectedDeck.pageCount),
    });
  }

  async function handleVisibilityOverride() {
    if (!selectedDeck) {
      return;
    }

    await navigateTo({
      manualVisiblePage: clampPage(
        Number(manualVisibleMaxPage),
        selectedDeck.pageCount,
      ),
      page: currentPage,
    });
  }

  return (
    <section className="slideshow-workspace">
      <article className="panel">
        <p className="eyebrow">Lot 10</p>
        <h2>Slideshow formateur</h2>
        {isLoading ? <p>Chargement des sessions actives...</p> : null}
        {activeSessions.length > 0 ? (
          <ul className="resource-list">
            {activeSessions.map((session) => (
              <li key={session.id}>
                <button
                  className={
                    session.id === selectedSessionId
                      ? "resource-button resource-button-active"
                      : "resource-button"
                  }
                  onClick={() => {
                    setShouldWriteTrainerHash(true);
                    setSelectedSessionId(session.id);
                  }}
                  type="button"
                >
                  <strong>{session.title}</strong>
                  <span>
                    {session.status} - tag {session.contentTagId ?? "non créé"}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p>Aucune session active accessible pour le slideshow.</p>
        )}
      </article>

      <article className="panel panel-wide">
        <p className="eyebrow">Navigation partagée</p>
        <h2>{selectedSession?.title ?? "Sélectionner une session active"}</h2>
        <div className="slideshow-layout">
          <div className="slideshow-main">
            <div className="slide-stage" aria-live="polite">
              <p className="eyebrow">{selectedDeck?.title ?? "Support"}</p>
              <strong>
                {selectedDeck
                  ? pageLabel(currentPage, selectedDeck.pageCount)
                  : "Aucun support"}
              </strong>
              <span>{runtime?.liveSummary ?? "Runtime non initialisé"}</span>
            </div>
            <div className="actions">
              <button
                className="button button-secondary"
                disabled={!mayNavigate || isSaving || currentPage <= 0}
                onClick={() => void navigateTo({ page: currentPage - 1 })}
                type="button"
              >
                Précédent
              </button>
              <button
                className="button button-primary"
                disabled={
                  !mayNavigate ||
                  isSaving ||
                  !selectedDeck ||
                  currentPage >= selectedDeck.pageCount
                }
                onClick={() => void navigateTo({ page: currentPage + 1 })}
                type="button"
              >
                Suivant
              </button>
              <button
                className="button button-secondary"
                disabled={!mayNavigate || isSaving || currentPage === 0}
                onClick={() => void navigateTo({ page: 0 })}
                type="button"
              >
                Accueil
              </button>
            </div>
            <form className="inline-form" onSubmit={handleTargetPage}>
              <label className="field">
                <span>Page cible</span>
                <input
                  disabled={!selectedDeck}
                  min="0"
                  max={selectedDeck?.pageCount ?? 0}
                  onChange={(event) => setTargetPage(event.target.value)}
                  type="number"
                  value={targetPage}
                />
              </label>
              <button
                className="button button-secondary"
                disabled={!mayNavigate || isSaving}
                type="submit"
              >
                Aller
              </button>
            </form>
          </div>

          <div className="slideshow-side">
            <h3>Supports publiés</h3>
            {decks.length > 0 ? (
              <ul className="resource-list">
                {decks.map((deck) => (
                  <li key={deck.id}>
                    <button
                      className={
                        deck.id === selectedDeckId
                          ? "resource-button resource-button-active"
                          : "resource-button"
                      }
                      onClick={() => {
                        setShouldWriteTrainerHash(true);
                        setSelectedDeckId(deck.id);
                      }}
                      type="button"
                    >
                      <strong>{deck.title}</strong>
                      <span>
                        {deck.visibleMaxPage} / {deck.pageCount} page(s) visible(s)
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p>Aucun support public dans cette session.</p>
            )}
          </div>
        </div>

        {selectedDeck ? (
          <div className="maintenance-block">
            <h3>Visibilité participant</h3>
            <p>
              La navigation augmente automatiquement la page maximale visible.
              Une baisse demande un override manuel.
            </p>
            <div className="actions">
              <button
                className="button button-secondary"
                disabled={!mayNavigate || isSaving}
                onClick={() =>
                  void navigateTo({
                    manualVisiblePage: selectedDeck.pageCount,
                    page: currentPage,
                  })
                }
                type="button"
              >
                Tout rendre visible
              </button>
              <button
                className="button button-secondary"
                disabled={!mayNavigate || isSaving}
                onClick={() =>
                  void navigateTo({
                    manualVisiblePage: currentPage,
                    page: currentPage,
                  })
                }
                type="button"
              >
                Aligner sur la page courante
              </button>
            </div>
            <div className="inline-form">
              <label className="field">
                <span>Override max visible</span>
                <input
                  disabled={!selectedDeck}
                  min="0"
                  max={selectedDeck.pageCount}
                  onChange={(event) =>
                    setManualVisibleMaxPage(event.target.value)
                  }
                  type="number"
                  value={manualVisibleMaxPage}
                />
              </label>
              <button
                className="button button-primary"
                disabled={
                  !mayNavigate ||
                  isSaving ||
                  manualVisibleMaxPage.trim().length === 0
                }
                onClick={() => void handleVisibilityOverride()}
                type="button"
              >
                Appliquer l'override
              </button>
            </div>
          </div>
        ) : null}

        <SessionQuestionnaireRuntimePanel sessionId={selectedSessionId} />

        <SessionExerciseRuntimePanel sessionId={selectedSessionId} />

        {message ? (
          <div className="alert alert-info" role="status">
            {message}
          </div>
        ) : null}
      </article>
    </section>
  );
}
